/* ─────────────────────────────────────────────────────────────────────────
   Kurtens — Stories player (vanilla, no build step)
   - <dialog> natif : top-layer (passe au-dessus de la sticky add-to-cart et
     du cart-drawer), focus-trap et Échap gratuits.
   - Barres de progression pilotées par requestAnimationFrame (image) ou par
     l'élément vidéo (currentTime/ended).
   - Zones tap (gauche=précédent, droite=suivant), appui long=pause, swipe
     horizontal=story, swipe bas=fermer.
   - Add-to-cart branché sur la machinerie du thème (cart-drawer) via import
     dynamique, avec repli window.location si indisponible.
   ───────────────────────────────────────────────────────────────────────── */

const HOLD_MS = 220; // seuil tap vs appui long
const SWIPE_PX = 60; // seuil de swipe

class KurtensStories extends HTMLElement {
  constructor() {
    super();
    this.reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    this.state = {
      index: -1,
      paused: false,
      rafId: null,
      start: 0,
      elapsed: 0,
      duration: 0,
    };
  }

  connectedCallback() {
    // Garde d'idempotence : si l'élément est DÉPLACÉ dans le DOM (ex. la popup
    // guide des tailles fait document.body.appendChild(popup) à l'ouverture),
    // disconnectedCallback puis connectedCallback se rejouent. Sans garde, tous
    // les listeners (dont le bouton son) seraient re-bindés -> double toggle ->
    // le son ne s'active jamais. Les listeners survivent au déplacement, on
    // n'initialise donc qu'une seule fois.
    if (this._kstInit) return;

    this.bubbles = Array.from(this.querySelectorAll(".kst__bubble"));
    this.player = this.querySelector(".kst__player");
    if (!this.bubbles.length || !this.player) return;

    this._kstInit = true;

    this.stage = this.player.querySelector("[data-kst-stage]");
    this.progressEl = this.player.querySelector("[data-kst-progress]");
    this.titleEl = this.player.querySelector("[data-kst-title]");
    this.overlayEl = this.player.querySelector("[data-kst-overlay]");
    this.footerEl = this.player.querySelector("[data-kst-footer]");
    this.muteBtn = this.player.querySelector("[data-kst-mute]");
    this.viewport = this.player.querySelector("[data-kst-viewport]");

    this.muted = true;

    // Données par story (lues sur les bulles)
    this.stories = this.bubbles.map((b) => ({ ...b.dataset }));

    // Listeners bulles
    this.bubbles.forEach((b, i) => {
      b.addEventListener("click", () => this.open(i));
      if (this.getSeen(i)) b.classList.add("is-seen");
    });

    // Contrôles player
    this.player
      .querySelector("[data-kst-close]")
      .addEventListener("click", () => this.player.close());
    // Les zones tap (data-kst-prev/next) sont gérées par la détection de
    // coordonnées dans bindGestures() — pas de listener click ici, sinon
    // double-avance (zone + viewport).
    this.player
      .querySelector("[data-kst-nav-prev]")
      .addEventListener("click", () => this.prev());
    this.player
      .querySelector("[data-kst-nav-next]")
      .addEventListener("click", () => this.next());
    this.muteBtn.addEventListener("click", () => this.toggleMute());

    this.player.addEventListener("close", () => this.onClose());
    this.player.addEventListener("keydown", (e) => this.onKeydown(e));

    this.bindGestures();
  }

  /* ---------- Ouverture / fermeture ---------- */

  open(index) {
    if (typeof this.player.showModal !== "function") {
      // Repli navigateurs sans <dialog> : on suit le lien produit
      const s = this.stories[index];
      if (s && (s.ctaUrl || s.productUrl))
        window.location = s.ctaUrl || s.productUrl;
      return;
    }
    document.documentElement.style.overflow = "hidden";
    this.player.showModal();
    this.render(index);
  }

  onClose() {
    this.stopTimer();
    this.teardownMedia();
    document.documentElement.style.overflow = "";
    this.state.index = -1;
    // Rendre le focus à la bulle d'origine
    const b = this.bubbles[this._lastIndex];
    if (b) b.focus();
  }

  /* ---------- Rendu d'une story ---------- */

  render(index) {
    if (index < 0 || index >= this.stories.length) return;
    this.stopTimer();
    this.teardownMedia();

    this.state.index = index;
    this._lastIndex = index;
    this.state.paused = false;
    this.markSeen(index);

    const s = this.stories[index];

    this.buildProgress();
    this.titleEl.textContent = s.heading || "";
    this.overlayEl.textContent = s.overlay || "";
    this.overlayEl.hidden = !s.overlay;
    this.buildFooter(s);

    // Navigation desktop : masquer les flèches aux extrémités
    this.player.querySelector("[data-kst-nav-prev]").disabled = index === 0;
    this.player.querySelector("[data-kst-nav-next]").disabled =
      index === this.stories.length - 1;

    if (s.mediaType === "video" && s.videoSrc) {
      this.renderVideo(s);
    } else {
      this.renderImage(s);
    }

    this.preload(index + 1);
  }

  renderVideo(s) {
    this.muteBtn.hidden = false;
    this.player.classList.toggle("is-muted", this.muted);
    const v = document.createElement("video");
    v.className = "kst__media";
    v.playsInline = true;
    v.muted = this.muted;
    v.setAttribute("playsinline", "");
    v.preload = "auto";
    if (s.poster) v.poster = s.poster;
    v.src = s.videoSrc;
    this.media = v;
    this.stage.appendChild(v);

    v.addEventListener("loadedmetadata", () => {
      this.state.duration = (v.duration || 5) * 1000;
    });
    v.addEventListener("timeupdate", () => {
      if (!v.duration) return;
      this.setProgress(this.state.index, v.currentTime / v.duration);
    });
    v.addEventListener("ended", () => this.next());

    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }

  renderImage(s) {
    this.muteBtn.hidden = true;
    const img = document.createElement("img");
    img.className = "kst__media";
    img.src = s.imageSrc || s.poster || "";
    img.alt = s.heading || "";
    this.media = img;
    this.stage.appendChild(img);

    this.state.duration = (parseFloat(s.duration) || 5) * 1000;
    if (this.reducedMotion) {
      // Pas d'auto-advance : on remplit la barre, l'utilisateur tape pour avancer
      this.setProgress(this.state.index, 1);
    } else {
      this.startTimer();
    }
  }

  teardownMedia() {
    if (this.media) {
      if (this.media.tagName === "VIDEO") {
        this.media.pause();
        this.media.removeAttribute("src");
        this.media.load();
      }
      this.media.remove();
      this.media = null;
    }
    this.stage.innerHTML = "";
  }

  /* ---------- Barres de progression ---------- */

  buildProgress() {
    this.progressEl.innerHTML = "";
    this.segments = this.stories.map((_, i) => {
      const seg = document.createElement("span");
      seg.className = "kst__seg";
      const fill = document.createElement("i");
      seg.appendChild(fill);
      if (i < this.state.index) fill.style.transform = "scaleX(1)";
      this.progressEl.appendChild(seg);
      return fill;
    });
  }

  setProgress(index, ratio) {
    const fill = this.segments && this.segments[index];
    if (fill)
      fill.style.transform = `scaleX(${Math.min(1, Math.max(0, ratio))})`;
  }

  startTimer() {
    this.state.start = performance.now();
    this.state.elapsed = 0;
    const loop = (now) => {
      if (this.state.paused) return;
      const elapsed = this.state.elapsed + (now - this.state.start);
      const ratio = elapsed / this.state.duration;
      this.setProgress(this.state.index, ratio);
      if (ratio >= 1) {
        this.next();
        return;
      }
      this.state.rafId = requestAnimationFrame(loop);
    };
    this.state.rafId = requestAnimationFrame(loop);
  }

  stopTimer() {
    if (this.state.rafId) cancelAnimationFrame(this.state.rafId);
    this.state.rafId = null;
  }

  /* ---------- Pause / reprise (appui long) ---------- */

  pause() {
    if (this.state.paused) return;
    this.state.paused = true;
    this.player.classList.add("is-paused");
    if (this.media && this.media.tagName === "VIDEO") {
      this.media.pause();
    } else {
      // fige l'avancement courant
      this.state.elapsed += performance.now() - this.state.start;
      this.stopTimer();
    }
  }

  resume() {
    if (!this.state.paused) return;
    this.state.paused = false;
    this.player.classList.remove("is-paused");
    if (this.media && this.media.tagName === "VIDEO") {
      const p = this.media.play();
      if (p && p.catch) p.catch(() => {});
    } else if (!this.reducedMotion) {
      this.state.start = performance.now();
      const loop = (now) => {
        if (this.state.paused) return;
        const elapsed = this.state.elapsed + (now - this.state.start);
        const ratio = elapsed / this.state.duration;
        this.setProgress(this.state.index, ratio);
        if (ratio >= 1) return this.next();
        this.state.rafId = requestAnimationFrame(loop);
      };
      this.state.rafId = requestAnimationFrame(loop);
    }
  }

  /* ---------- Navigation ---------- */

  next() {
    const last = this.state.index === this.stories.length - 1;
    if (last) {
      if (this.dataset.endBehavior === "loop") return this.render(0);
      return this.player.close();
    }
    this.render(this.state.index + 1);
  }

  prev() {
    if (this.state.index <= 0) return this.render(0);
    this.render(this.state.index - 1);
  }

  preload(index) {
    const s = this.stories[index];
    if (!s) return;
    if (s.mediaType === "video" && s.videoSrc) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = s.videoSrc;
    } else if (s.imageSrc) {
      const img = new Image();
      img.src = s.imageSrc;
    }
  }

  /* ---------- Pied : achat / lien ---------- */

  buildFooter(s) {
    this.footerEl.innerHTML = "";
    const addEnabled = this.dataset.addToCart === "true";

    // Lien explicite -> ancre
    if (s.ctaUrl) {
      const a = document.createElement("a");
      a.className = "kst__cta";
      a.href = s.ctaUrl;
      a.textContent = s.ctaLabel || this.dataset.ctaDefault || "Découvrir";
      this.footerEl.appendChild(a);
      return;
    }

    // Achat
    if (addEnabled && s.variantId && s.variantAvailable === "true") {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "kst__cta";
      const label =
        s.ctaLabel || this.dataset.ctaDefault || "Ajouter au panier";
      btn.innerHTML = `<span class="kst__cta-label">${label}</span>${
        s.variantPrice
          ? `<span class="kst__cta-price">${s.variantPrice}</span>`
          : ""
      }`;
      btn.addEventListener("click", () => this.addToCart(s, btn));
      this.footerEl.appendChild(btn);
    } else if (s.productUrl) {
      const a = document.createElement("a");
      a.className = "kst__cta";
      a.href = s.productUrl;
      a.textContent = s.ctaLabel || "Voir le produit";
      this.footerEl.appendChild(a);
    }
  }

  async addToCart(s, btn) {
    // Variant : si la story pointe le produit de la page, on lit le variant
    // sélectionné en live (taille / couleur du visiteur).
    let variantId = s.variantId;
    if (s.usePageVariant === "true") {
      const live = document.querySelector(
        'product-form [name="id"], form[action*="/cart/add"] [name="id"]',
      );
      if (live && live.value) variantId = live.value;
    }
    if (!variantId) return;

    btn.classList.add("loading");
    btn.disabled = true;
    this.pause();

    const cart =
      document.querySelector("cart-drawer") ||
      document.querySelector("main-cart");
    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", "1");
    if (cart && cart.getSectionsToRender) {
      formData.append(
        "sections",
        cart
          .getSectionsToRender()
          .map((x) => x.id)
          .join(","),
      );
      formData.append("sections_url", window.location.pathname);
    }

    try {
      const res = await fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/javascript",
        },
        body: formData,
      });
      const data = await res.json();

      if (data.status) {
        await this.notify(data.description || data.message, "error");
        return;
      }

      if (cart && cart.renderContents) {
        cart.renderContents(data);
        this.player.close();
        try {
          const { eventModal } = await import("@NextSkyTheme/modal");
          eventModal(cart, "open", false, "delay");
        } catch (e) {
          /* drawer indisponible : rien, le panier est à jour */
        }
      } else {
        window.location = window.routes.cart_url;
      }
    } catch (e) {
      window.location = window.routes.cart_url;
    } finally {
      btn.classList.remove("loading");
      btn.disabled = false;
    }
  }

  async notify(message, type) {
    try {
      const { notifier } = await import("@NextSkyTheme/notification");
      notifier.show(message, type, 4000);
    } catch (e) {
      console.error("[kurtens-stories]", message);
    }
  }

  /* ---------- Mute ---------- */

  toggleMute() {
    this.muted = !this.muted;
    if (this.media && this.media.tagName === "VIDEO")
      this.media.muted = this.muted;
    this.player.classList.toggle("is-muted", this.muted);
    this.muteBtn.setAttribute(
      "aria-label",
      this.muted ? "Activer le son" : "Couper le son",
    );
  }

  /* ---------- Clavier ---------- */

  onKeydown(e) {
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        this.next();
        break;
      case "ArrowLeft":
        e.preventDefault();
        this.prev();
        break;
      case " ":
        e.preventDefault();
        this.state.paused ? this.resume() : this.pause();
        break;
      // Échap : géré nativement par <dialog>
    }
  }

  /* ---------- Gestes (tap zones, hold, swipe) ---------- */

  bindGestures() {
    let holdTimer = null;
    let startX = 0;
    let startY = 0;
    let moved = false;
    let held = false;

    const onDown = (e) => {
      if (e.target.closest(".kst__footer, .kst__topbar, .kst__nav")) return;
      startX = e.clientX;
      startY = e.clientY;
      moved = false;
      held = false;
      holdTimer = setTimeout(() => {
        held = true;
        this.pause();
      }, HOLD_MS);
    };

    const onMove = (e) => {
      if (
        Math.abs(e.clientX - startX) > 10 ||
        Math.abs(e.clientY - startY) > 10
      )
        moved = true;
    };

    const onUp = (e) => {
      clearTimeout(holdTimer);
      if (e.target.closest(".kst__footer, .kst__topbar, .kst__nav")) return;

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      // Swipe bas -> fermer
      if (dy > SWIPE_PX && Math.abs(dy) > Math.abs(dx)) {
        this.player.close();
        return;
      }
      // Swipe horizontal -> story
      if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
        dx < 0 ? this.next() : this.prev();
        return;
      }
      // Appui long terminé -> reprendre
      if (held) {
        this.resume();
        return;
      }
      // Tap simple -> zone gauche/droite
      if (!moved) {
        const rect = this.viewport.getBoundingClientRect();
        const x = e.clientX - rect.left;
        x < rect.width / 3 ? this.prev() : this.next();
      }
    };

    this.viewport.addEventListener("pointerdown", onDown);
    this.viewport.addEventListener("pointermove", onMove);
    this.viewport.addEventListener("pointerup", onUp);
    this.viewport.addEventListener("pointercancel", () => {
      clearTimeout(holdTimer);
      if (held) this.resume();
    });
  }

  /* ---------- État vu / non-vu (localStorage) ---------- */

  seenKey() {
    return "kst-seen-" + (this.dataset.sectionId || this.id || "default");
  }

  getSeenSet() {
    try {
      return new Set(JSON.parse(localStorage.getItem(this.seenKey()) || "[]"));
    } catch (e) {
      return new Set();
    }
  }

  getSeen(i) {
    return this.getSeenSet().has(
      String(this.stories[i] && this.stories[i].kstIndex),
    );
  }

  markSeen(i) {
    const id = this.stories[i] && this.stories[i].kstIndex;
    if (id == null) return;
    const set = this.getSeenSet();
    set.add(String(id));
    try {
      localStorage.setItem(this.seenKey(), JSON.stringify(Array.from(set)));
    } catch (e) {}
    if (this.bubbles[i]) this.bubbles[i].classList.add("is-seen");
  }
}

if (!customElements.get("kurtens-stories")) {
  customElements.define("kurtens-stories", KurtensStories);
}
